export const extractNumber = (value: any): number | string => {
    // If already a number, return as-is
    if (typeof value === 'number') {
        return value;
    }

    const str = String(value).trim();

    // Try direct number conversion first
    const directNum = Number(str);
    if (!isNaN(directNum)) {
        return directNum;
    }

    // Extract number using regex - handles various formats:
    // "$4.99", "USD 8.00", "500 KB", "25%", "1,234.56", "-123.45"
    const numberMatch = str.match(/[-+]?\s*[0-9,]+(?:\.[0-9]*)?/);
    if (numberMatch) {
        const numStr = numberMatch[0].replace(/[,\s]/g, ''); // Remove commas and spaces
        const extractedNum = parseFloat(numStr);
        if (!isNaN(extractedNum)) {
            return extractedNum;
        }
    }

    // If no number found, return original string
    return value;
};

// Helper to remove cycles from Sankey data (DAG requirement)
export const sanitizeSankeyData = (links: any[]) => {
    if (!links || links.length === 0) return [];

    // 1. Remove self-loops (A -> A)
    let safeLinks = links.filter(l => l.source !== l.target);

    // 2. Remove cycles (back-edges) using DFS
    const adj = new Map<string, string[]>();
    safeLinks.forEach(l => {
        if (!adj.has(l.source)) adj.set(l.source, []);
        adj.get(l.source)?.push(l.target);
    });

    const visited = new Set<string>();
    const recStack = new Set<string>();
    const badEdges = new Set<string>(); // "source|target"

    const hasCycle = (node: string) => {
        visited.add(node);
        recStack.add(node);

        const neighbors = adj.get(node) || [];
        for (const neighbor of neighbors) {
            if (!visited.has(neighbor)) {
                hasCycle(neighbor);
            } else if (recStack.has(neighbor)) {
                // Cycle detected: node -> neighbor is a back edge
                badEdges.add(`${node}|${neighbor}`);
            }
        }

        recStack.delete(node);
    };

    // Prioritize traversal order: Start node -> Nodes with 0 in-degree -> Others
    const nodes = new Set<string>();
    const inDegree = new Map<string, number>();
    
    safeLinks.forEach(l => {
        nodes.add(l.source);
        nodes.add(l.target);
        inDegree.set(l.target, (inDegree.get(l.target) || 0) + 1);
        if (!inDegree.has(l.source)) inDegree.set(l.source, 0);
    });

    const sortedNodes = Array.from(nodes).sort((a, b) => {
        if (a === 'Start') return -1;
        if (b === 'Start') return 1;
        return (inDegree.get(a) || 0) - (inDegree.get(b) || 0);
    });

    sortedNodes.forEach(node => {
        if (!visited.has(node)) {
            hasCycle(node);
        }
    });

    return safeLinks.filter(l => !badEdges.has(`${l.source}|${l.target}`));
};
